import type {
  ApiErrorPayload,
  ApiSuccess,
  MeResponse,
  PasswordPolicyResponse,
  SetupStatus,
} from "@edm/shared";
import { CSRF_COOKIE } from "@edm/shared";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly errors: ApiErrorPayload["errors"];

  constructor(
    payload: Partial<Omit<ApiErrorPayload, "requestId">> & {
      status: number;
      requestId?: string | null;
    },
  ) {
    super(payload.detail ?? "请求未完成");
    this.name = "ApiError";
    this.status = payload.status;
    this.code = payload.code ?? `HTTP_${payload.status}`;
    this.requestId = payload.requestId ?? null;
    this.errors = payload.errors;
  }
}

export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      try {
        return decodeURIComponent(trimmed.slice(prefix.length));
      } catch {
        return null;
      }
    }
  }
  return null;
}

const CSRF_HEADER = "X-CSRF-Token";

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const method = (init.method ?? "GET").toUpperCase();
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (method !== "GET" && method !== "HEAD") {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }
  const response = await fetch(path, {
    ...init,
    headers,
    credentials: "include",
  });
  const payload = (await response.json().catch(() => null)) as
    | (ApiSuccess<T> & Partial<ApiErrorPayload>)
    | null;
  if (!response.ok) {
    if (
      response.status === 401 &&
      !path.startsWith("/api/v1/auth/login") &&
      !path.startsWith("/api/v1/setup/bootstrap")
    ) {
      window.dispatchEvent(new CustomEvent("edm:unauthorized"));
    }
    throw new ApiError({
      status: response.status,
      code: payload && "code" in payload ? payload.code : undefined,
      detail: payload && "detail" in payload ? payload.detail : "请求未完成，请稍后重试",
      requestId: payload && "requestId" in payload ? (payload.requestId ?? undefined) : undefined,
      errors: payload && "errors" in payload ? payload.errors : undefined,
    });
  }
  return (payload?.data as T) ?? (null as T);
}

export function login(
  identifier: string,
  password: string,
  turnstileToken?: string | null,
): Promise<MeResponse> {
  return apiFetch<MeResponse>("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ identifier, password, turnstileToken: turnstileToken ?? null }),
  }).catch((error: unknown) => {
    if (error instanceof ApiError && error.status === 401) {
      throw new ApiError({
        status: 401,
        code: error.code,
        detail: "登录失败，请核对账号和密码后重试",
        requestId: error.requestId ?? undefined,
      });
    }
    throw error;
  });
}

export async function fetchMe(): Promise<MeResponse | null> {
  try {
    return await apiFetch<MeResponse>("/api/v1/auth/me");
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function logout(): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>("/api/v1/auth/logout", { method: "POST" });
}

export function changePassword(currentPassword: string, newPassword: string): Promise<MeResponse> {
  return apiFetch<MeResponse>("/api/v1/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function getSetupStatus(): Promise<SetupStatus> {
  return apiFetch<SetupStatus>("/api/v1/setup/status");
}

export function bootstrap(input: {
  setupToken: string;
  displayName: string;
  identifierType: "phone" | "email" | "adid";
  identifierValue: string;
  password: string;
}): Promise<MeResponse> {
  return apiFetch<MeResponse>("/api/v1/setup/bootstrap", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function getPasswordPolicy(): Promise<PasswordPolicyResponse> {
  return apiFetch<PasswordPolicyResponse>("/api/v1/auth/password-policy");
}
